import { Controller } from "react-hook-form";
import FileTree from "files/components/FileTree";
import { JobType } from "files/types/interfaces/fileJob";
import { CircleAlertIcon } from "lucide-react";
import { Alert, AlertTitle } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "~/components/ui/dialog";
import { Input } from "~/components/ui/input";
import { Label } from "~/components/ui/label";
import { TextOverflow } from "~/components/ui/text-overflow";
import { UseFileActionDialog, useFileActionDialog } from "~/hooks/useFileActionDialog";

function RenameFileInput({
  form,
  originalName,
  fileSystemNodeName,
  filesData,
  submitting,
  inputRef,
  isDirectory,
}: UseFileActionDialog & { isDirectory: boolean }) {
  return (
    <Controller
      control={form.control}
      name="fileSystemNodeName"
      rules={{
        required: { value: true, message: "Required" },
        validate: {
          newName: (value: string) => {
            if (value === originalName) {
              return "Please enter a new name";
            }
          },
          validName: (value: string) => {
            if (value.includes("/")) {
              return `${fileSystemNodeName} name cannot contain "/"`;
            }
          },
          uniqueName: (value: string) => {
            // Get keys of existing subdirectories
            const siblings = filesData?.children?.map((file) => file.key);

            const objectKey = `${filesData?.current_file?.key}${value}${isDirectory ? "/" : ""}`;

            if (siblings?.includes(objectKey)) {
              return `${fileSystemNodeName} already exists`;
            }
          },
        },
      }}
      render={({ field, fieldState: { error } }) => (
        <div className="flex flex-col gap-1">
          <Label htmlFor={field.name} className="text-neutral-900/50">
            New Name *
          </Label>
          <Input
            {...field}
            className="w-full"
            autoComplete="off"
            autoFocus
            disabled={submitting}
            aria-invalid={!!error}
            id={field.name}
            ref={inputRef}
          />
          <p className="text-destructive text-xs leading-[19px] empty:h-[19px]">
            {error?.message ?? ""}
          </p>
        </div>
      )}
    />
  );
}

interface FileActionDialogProps {
  open: boolean;
  action: JobType;
  isDirectory: boolean;
  handleOpenChange: (value: boolean) => void;
  fileKey: string;
}

export const FileActionDialog = ({
  open,
  action,
  isDirectory,
  handleOpenChange,
  fileKey,
}: FileActionDialogProps) => {
  const fileActionDialogHandlers = useFileActionDialog({
    action,
    fileKey,
    isDirectory,
    handleClose: () => handleOpenChange(false),
  });

  const {
    selectedFile,
    setSelectedFile,
    fileSystemNodeName,
    handleSubmit,
    submitDisabled,
    submitting,
    form,
    error,
  } = fileActionDialogHandlers;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent showCloseButton={false} className="pt-4 pb-5">
        <DialogHeader>
          <DialogTitle className="text-base">
            <TextOverflow className="block first-letter:capitalize">
              {action === JobType.RENAME
                ? `${action} ${fileSystemNodeName}`
                : `${action} ${fileSystemNodeName} to...`}
            </TextOverflow>
          </DialogTitle>
        </DialogHeader>
        <form className="flex flex-col gap-5" onSubmit={handleSubmit}>
          <div className="flex flex-col gap-4">
            {error && (
              <Alert variant="destructive">
                <CircleAlertIcon className="stroke-destructive" />
                <AlertTitle>{error}</AlertTitle>
              </Alert>
            )}
            {action === JobType.RENAME ? (
              <RenameFileInput isDirectory={isDirectory} {...fileActionDialogHandlers} />
            ) : (
              <FileTree
                checkWriteAccess
                selectedFile={selectedFile}
                setSelectedFile={setSelectedFile}
              />
            )}
          </div>
          <div className="flex items-center justify-end gap-4">
            <Button variant="outline" onClick={() => handleOpenChange(false)}>
              Cancel
            </Button>
            <Button
              type="submit"
              loading={submitting}
              disabled={action === JobType.RENAME ? !form.formState.isValid : submitDisabled}
            >
              Confirm
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
